import type {
  AreaComponent,
  Placement,
  PlacementPosition,
  Surface,
  SurfacePoint,
} from '@repo/api-contracts';
import { type PointerEvent, type RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { clientToPhotoPoint, type PhotoDimensions, surfacePhotoToMm } from '@/entities/edit-area';
import { clampToSurface } from './clamp-to-surface';
import { anchoredClient, type SvgAnchor, svgAnchorOf } from './svg-drag-anchor';

interface Options {
  svgRef: RefObject<SVGSVGElement | null>;
  photoSize: PhotoDimensions;
  disabled: boolean;
  onMove: (componentId: string, position: Placement['position']) => void;
}
interface Preview {
  componentId: string;
  position: Placement['position'];
}
interface Drag {
  pointerId: number;
  /** 잡은 점의 표면 mm. 현재 점의 mm 와의 차를 배치 위치에 더한다. */
  origin: PlacementPosition;
  /** 누른 화면 좌표. 그 자리에서 놓으면(선택만 한 누르기) 배치를 바꾸지 않는다. */
  client: { x: number; y: number };
  /** 누른 때 svg 의 화면 위치 — 끄는 동안 사진 칸이 움직여도 화면에서 끈 거리만 쓴다. */
  anchor: SvgAnchor;
  component: AreaComponent;
  surface: Surface;
  placement: Placement;
  /** 마지막으로 계산한 유효 위치. 실루엣 밖(역변환 null)에서는 바뀌지 않고, 유효한 이동이 없었으면 null 이다. */
  last: Preview | null;
  /** 옮기지 않고 누르기만 하고 놓았을 때 부른다(이미 고른 영역을 다시 누르면 점 편집). */
  onClick: (() => void) | undefined;
}

/** 누르기만 한 것으로 보는 포인터 흔들림(화면 px). */
const CLICK_SLOP_PX = 3;

/**
 * 선택만 한 누르기인지. 포인터가 거의 움직이지 않았고 맞춘 위치가 원래 위치에서 1mm 미만이면, 1mm 맞춤만으로
 * 소수 위치(예: 원통 기본 7.334mm)가 옮겨지지 않게 이동을 건너뛴다.
 */
const isClick = (
  current: Drag,
  event: PointerEvent<SVGElement>,
  position: PlacementPosition,
): boolean =>
  Math.hypot(event.clientX - current.client.x, event.clientY - current.client.y) <= CLICK_SLOP_PX &&
  Math.abs(position.xMm - current.placement.position.xMm) < 1 &&
  Math.abs(position.yMm - current.placement.position.yMm) < 1;

export const usePlacementPointer = ({ svgRef, photoSize, disabled, onMove }: Options) => {
  const drag = useRef<Drag | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const cancel = useCallback((): void => {
    const pointerId = drag.current?.pointerId;
    drag.current = null;
    setPreview(null);
    if (pointerId !== undefined && svgRef.current?.hasPointerCapture(pointerId))
      svgRef.current.releasePointerCapture(pointerId);
  }, [svgRef]);
  // External selection/version changes remount the Canvas; locking also invalidates pending input.
  useEffect(() => {
    if (disabled) cancel();
  }, [disabled, cancel]);

  const start = (
    event: PointerEvent<SVGElement>,
    component: AreaComponent,
    surface: Surface,
    placement: Placement,
    onClick?: () => void,
  ): void => {
    if (
      disabled ||
      event.button !== 0 ||
      svgRef.current === null ||
      photoSize.widthPx <= 0 ||
      photoSize.heightPx <= 0
    )
      return;
    const point = clientToPhotoPoint(svgRef.current, { x: event.clientX, y: event.clientY });
    const origin = point === null ? null : surfacePhotoToMm(surface, photoSize, point);
    if (origin === null || clampToSurface(component, surface, placement.position) === null) return;
    event.preventDefault();
    event.stopPropagation();
    drag.current = {
      pointerId: event.pointerId,
      origin,
      client: { x: event.clientX, y: event.clientY },
      anchor: svgAnchorOf(svgRef.current),
      component,
      surface,
      placement,
      last: null,
      onClick,
    };
    svgRef.current.setPointerCapture(event.pointerId);
  };
  /** 끌고 있는 포인터 이벤트의 사진 px 점. 잠금·다른 포인터·svg 없음·화면 행렬 없음이면 null. */
  const pointOf = (event: PointerEvent<SVGElement>): SurfacePoint | null => {
    const current = drag.current;
    if (
      disabled ||
      current === null ||
      event.pointerId !== current.pointerId ||
      svgRef.current === null
    )
      return null;
    return clientToPhotoPoint(
      svgRef.current,
      anchoredClient(svgRef.current, current.anchor, { x: event.clientX, y: event.clientY }),
    );
  };
  /** 사진 점에 놓았을 때의 배치 위치. 실루엣 밖(역변환 null)이면 null — 그때는 마지막 유효 위치를 쓴다(결정 Q5). */
  const positionAt = (current: Drag, point: SurfacePoint): Preview | null => {
    const to = surfacePhotoToMm(current.surface, photoSize, point);
    if (to === null) return null;
    // 1mm 로 맞춘 뒤 표면 규칙 안으로 자른다 — 끝에 붙은 위치는 표면 끝값(소수일 수 있다)이 된다.
    const position = clampToSurface(current.component, current.surface, {
      xMm: Math.round(current.placement.position.xMm + to.xMm - current.origin.xMm),
      yMm: Math.round(current.placement.position.yMm + to.yMm - current.origin.yMm),
    });
    return position === null ? null : { componentId: current.component.id, position };
  };
  const move = (event: PointerEvent<SVGElement>): void => {
    const current = drag.current;
    const point = pointOf(event);
    if (current === null || point === null) return;
    const next = positionAt(current, point);
    if (next === null) return;
    drag.current = { ...current, last: next };
    setPreview(next);
  };
  const finish = (event: PointerEvent<SVGElement>): void => {
    const current = drag.current;
    if (current === null || event.pointerId !== current.pointerId) return;
    const point = pointOf(event);
    // 실루엣 밖에서 놓으면 마지막 유효 위치로 확정한다(결정 Q5). 유효한 이동이 없었으면 배치는 그대로다.
    const next = point === null ? null : (positionAt(current, point) ?? current.last);
    cancel();
    const clicked =
      next === null
        ? Math.hypot(event.clientX - current.client.x, event.clientY - current.client.y) <=
          CLICK_SLOP_PX
        : isClick(current, event, next.position);
    if (clicked) current.onClick?.();
    if (next === null || clicked) return;
    onMove(next.componentId, next.position);
  };
  return { preview, start, move, finish, cancel };
};
